import Accordion from "@/components/Accordion";
import { tv } from "tailwind-variants";
import { Caption } from "@/components/Typography";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromLine } from "@fortawesome/pro-duotone-svg-icons";

import klaviyoLogo from "@/assets/images/collect/klaviyo-logo.png";
import shopifyLogo from "@/assets/images/collect/shopify-logo.png";
import datashipsLogo from "@/assets/images/collect/dataships-logo.png";

const styles = tv({
  slots: {
    gridRow: "grid w-full grid-cols-4 grid-rows-1 gap-30",
    gridRowFill: "col-span-3",
    caption: "font-semibold uppercase !leading-[25px]",
    statRow: "flex flex-row justify-between gap-20 sm:items-center",
    statText:
      "text-[22px] font-bold leading-[35px] text-green sm:text-[30px] sm:leading-[50px]",
    statTextIcon: "h-25 text-gray-dark",
    subGrid:
      "grid-rows-auto grid grid-cols-1 gap-10 sm:grid-cols-4 sm:grid-rows-1 sm:gap-30",
    subStatText: "text-[21px] font-bold leading-[35px]",
    subStat: "flex flex-col items-start gap-10",
    statLabel: "font-semibold text-gray-dark",
  },
  variants: {
    hideOnMobile: {
      true: {
        gridRowFill: "hidden sm:block",
      },
    },
    isHeader: {
      true: {
        gridRow: "[&>*:first-child]:col-span-4 sm:[&>*:first-child]:col-span-1",
      },
    },
    isStat: {
      true: {
        gridRow: "grid-cols-2 sm:grid-cols-4",
        gridRowFill: "col-span-1 sm:col-span-3",
      },
    },
  },
});

interface IProps {
  data: IGetUsageDashboardRS;
}

const OutputAndSyncs = ({ data }: IProps) => {
  
  const { t } = useTranslation();
  const {
    gridRow,
    gridRowFill,
    caption,
    statRow,
    statText,
    statTextIcon,
    statLabel,
    subGrid,
    subStat,
    subStatText,
  } = styles();

  const {
    numUpdates,
    numKlaviyoListMovements,
    numKlaviyoPropertyUpdates,
    numShopifyStatusUpdates,
    numComplianceLogs,
    shouldDisplayShopifyStatusUpdates
  } = data;

  const renderTitle = () => (
    <div className={gridRow({ isHeader: true })}>
      <Caption className={caption()}>
        {t("email.usage.outputAndSyncs.titleLeft")}
      </Caption>
      <div className={gridRowFill({ hideOnMobile: true })}>
        <Caption className={caption()}>
          {t("email.usage.outputAndSyncs.titleRight")}
        </Caption>
      </div>
    </div>
  );

  const renderLeftStat = ({
    value,
    label,
  }: {
    value: number;
    label: string;
  }) => (
    <div className={statRow()}>
      <div>
        <p className={statText()} data-testid="usage-dashboard-stat">
          {value.toLocaleString()}
        </p>
        <Caption className={statLabel()}>{label}</Caption>
      </div>
      <FontAwesomeIcon
        fixedWidth
        icon={faArrowRightFromLine}
        className={statTextIcon()}
      />
    </div>
  );

  const renderIndividualStat = ({
    icon,
    value,
    label,
  }: {
    icon: "klaviyo" | "shopify" | "dataships";
    value: number;
    label: string;
  }) => {
    const getIcon = () => {
      const mapping = {
        klaviyo: klaviyoLogo,
        shopify: shopifyLogo,
        dataships: datashipsLogo,
      };

      return mapping[icon] || klaviyoLogo;
    };

    return (
      <div className={subStat()}>
        <img src={getIcon()} height={18} width={18} />
        <p className={subStatText()} data-testid="usage-dashboard-stat">
          {value.toLocaleString()}
        </p>
        <Caption className={statLabel()}>{label}</Caption>
      </div>
    );
  };

  const renderBody = () => (
    <div>
      <div className={gridRow({ isStat: true })}>
        {renderLeftStat({
          value: numUpdates,
          label: t("email.usage.outputAndSyncs.stats.resultingUpdates"),
        })}
        <div className={gridRowFill({ isStat: true })}>
          <div className={subGrid()}>
            {renderIndividualStat({
              icon: "klaviyo",
              value: numKlaviyoListMovements,
              label: t("email.usage.outputAndSyncs.stats.klaviyoListMovements"),
            })}
            {renderIndividualStat({
              icon: "klaviyo",
              value: numKlaviyoPropertyUpdates,
              label: t(
                "email.usage.outputAndSyncs.stats.klaviyoPropertyUpdates",
              ),
            })}
            {shouldDisplayShopifyStatusUpdates &&
              renderIndividualStat({
              icon: "shopify",
              value: numShopifyStatusUpdates,
              label: t("email.usage.outputAndSyncs.stats.shopifyUpdates"),
            })}
            {renderIndividualStat({
              icon: "dataships",
              value: numComplianceLogs,
              label: t("email.usage.outputAndSyncs.stats.auditLogs"),
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Accordion
      topContent={renderTitle()}
      hasShadow={false}
      defaultIsExpanded
      hideToggle>
      {renderBody()}
    </Accordion>
  );
};

export default OutputAndSyncs;
